import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Clock, QrCode, Receipt, ShoppingBag, UserRound, UtensilsCrossed } from 'lucide-react';
import { WEEKDAYS, minutesToHHMM } from '@garlic/shared';
import { ApiError, post } from '../../lib/api';
import { formatBRL } from '../../lib/format';
import { Alert, Button, GarlicMark, Spinner } from '../../ui/primitives';
import { useToast } from '../../ui/feedback';
import { useCart } from '../../menu/cart';
import { MenuBrowser } from '../../menu/MenuBrowser';
import { ProductSheet } from '../../menu/ProductSheet';
import { CartSheet, type SubmitPayload } from '../../menu/CartSheet';
import type { MenuCategory, MenuProduct } from '../../menu/types';
import { RestaurantAvatar } from '../RestaurantBadge';
import type { TenantRestaurant } from '../TenantApp';
import { useGuestSession, usePublicMenu } from './guest';
import { useGuestRealtime } from '../../lib/realtime';
import { staffPath, publicPath } from '../../lib/paths';

/** Cardápio público: destino do QR Code da mesa. Sem mesa identificada, funciona só para consulta. */
export function PublicMenuPage({ restaurant: r }: { restaurant: TenantRestaurant }) {
  const menu = usePublicMenu();
  const guest = useGuestSession();
  useGuestRealtime(guest.isSuccess);
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const joined = (location.state as { joined?: string } | null)?.joined;
  const cart = useCart(`garlic:cart:${r.slug}:${guest.data?.session.number ?? 'none'}`);
  const [selected, setSelected] = useState<{ p: MenuProduct; c: MenuCategory } | null>(null);
  const [cartOpen, setCartOpen] = useState(false);

  if (r.status === 'SUSPENDED') return <Unavailable r={r} />;
  if (menu.isPending) return <Spinner />;
  if (menu.isError) return <Unavailable r={r} message="Não foi possível carregar o cardápio. Tente novamente em instantes." />;

  const m = menu.data;
  const hasTable = guest.isSuccess;
  const blockedReason = !m.ordering.acceptOrders
    ? r.status === 'ONBOARDING'
      ? 'O cardápio digital ainda não está ativo.'
      : 'Pedidos pelo celular pausados. Chame um atendente.'
    : m.ordering.blockedByHours
      ? 'O restaurante está fechado agora.'
      : !hasTable
        ? 'Escaneie o QR Code da sua mesa para pedir.'
        : undefined;
  const canOrder = !blockedReason;

  const submit = (payload: SubmitPayload) => post<{ order: { number: number } }>('/api/public/orders', payload);

  return (
    <div className="min-h-dvh pb-28">
      <header className="relative overflow-hidden bg-[var(--brand)] text-[var(--brand-contrast)]">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)', backgroundSize: '18px 18px' }} aria-hidden />
        <div className="relative mx-auto flex max-w-2xl items-center justify-end gap-2 px-4 pb-12 pt-4">
          <Link to={staffPath('/login')} className="inline-flex items-center gap-1.5 rounded-full border border-current/25 px-3 py-1.5 text-xs font-medium hover:bg-white/10">
            <UserRound className="size-3.5" aria-hidden /> Área do Funcionário
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto -mt-9 max-w-2xl px-4">
        <RestaurantAvatar r={r} className="size-[4.5rem] ring-4 ring-paper" />
        <h1 className="mt-3 font-display text-2xl font-semibold leading-tight">{r.name}</h1>
        <OpenStatus open={m.ordering.openNow} hours={m.hours} />

        <div className="mt-4 space-y-3">
          {hasTable ? (
            <Link to={publicPath('/mesa')} className="flex items-center gap-3 rounded-2xl border border-line bg-white px-4 py-3 shadow-card hover:bg-paper/60">
              <span className="grid size-10 place-items-center rounded-xl bg-paper-2"><Receipt className="size-5 text-ink-2" /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">Mesa {guest.data.session.tableLabel}</span>
                <span className="block text-xs text-ink-3">
                  {guest.data.orders.length ? `${guest.data.orders.length} pedido(s) · ${formatBRL(guest.data.totals.consumedCents)}` : 'Nenhum pedido ainda'}
                </span>
              </span>
              <span className="text-sm font-medium text-[var(--brand)]">Minha mesa</span>
            </Link>
          ) : (
            m.ordering.acceptOrders && (
              <div className="flex items-center gap-3 rounded-2xl border border-dashed border-line bg-white/60 px-4 py-3 text-sm text-ink-2">
                <QrCode className="size-5 shrink-0 text-ink-3" /> Escaneie o QR Code da sua mesa para fazer pedidos.
              </div>
            )
          )}
          {joined && <Alert tone="success">Você está na mesa {joined}. Bom apetite!</Alert>}
          {m.ordering.notice && <Alert tone="info">{m.ordering.notice}</Alert>}
          {hasTable && blockedReason && <Alert tone="warning">{blockedReason}</Alert>}
        </div>

        {m.categories.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-line bg-white p-8 text-center shadow-card">
            <UtensilsCrossed className="mx-auto size-6 text-ink-3" />
            <p className="mt-2 font-semibold">O cardápio digital está sendo preparado</p>
            <p className="text-sm text-ink-3">Enquanto isso, chame um atendente.</p>
          </div>
        ) : (
          <div className="mt-2">
            <MenuBrowser categories={m.categories} onSelect={(p, c) => setSelected({ p, c })} />
          </div>
        )}

        <footer className="flex items-center justify-center gap-1.5 py-10 text-xs text-ink-3">
          <GarlicMark className="size-4" /> Pedidos com Garlic
        </footer>
      </main>

      {cart.count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <Button variant="brand" size="lg" className="mx-auto flex w-full max-w-2xl justify-between shadow-xl" onClick={() => setCartOpen(true)}>
            <span className="inline-flex items-center gap-2"><ShoppingBag className="size-5" /> Ver carrinho ({cart.count})</span>
            <span className="tabular-nums">{formatBRL(cart.totalCents)}</span>
          </Button>
        </div>
      )}

      <ProductSheet
        product={selected?.p ?? null}
        category={selected?.c ?? null}
        canOrder={canOrder}
        blockedReason={blockedReason}
        onClose={() => setSelected(null)}
        onAdd={(line) => {
          cart.add(line);
          toast.success(`${line.quantity}× ${line.name} no carrinho`);
        }}
      />
      <CartSheet
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        onSubmit={async (payload) => {
          try {
            return await submit(payload);
          } catch (err) {
            if (err instanceof ApiError && (err.status === 401 || err.status === 410)) void qc.invalidateQueries({ queryKey: ['guest-session'] });
            throw err;
          }
        }}
        onSuccess={(n) => {
          setCartOpen(false);
          void qc.invalidateQueries({ queryKey: ['guest-session'] });
          void qc.invalidateQueries({ queryKey: ['public-menu'] });
          navigate(publicPath('/mesa'), { state: { sent: n } });
        }}
      />
    </div>
  );
}

function OpenStatus({ open, hours }: { open: boolean | null; hours: { weekday: number; opensAt: number; closesAt: number }[] }) {
  // Sem horários cadastrados o sistema não sabe se está aberto: não afirma nada.
  if (open === null) return null;
  return (
    <details className="mt-1 text-sm">
      <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-ink-3">
        <span className={`size-2 rounded-full ${open ? 'bg-sprout-500' : 'bg-danger-600'}`} />
        {open ? 'Aberto agora' : 'Fechado agora'} · <span className="underline decoration-dotted">ver horários</span>
      </summary>
      <ul className="mt-2 space-y-0.5 rounded-xl border border-line bg-white p-3 text-xs">
        {WEEKDAYS.map((d, i) => {
          const hs = hours.filter((h) => h.weekday === i);
          return (
            <li key={d} className="flex justify-between gap-4">
              <span className="text-ink-3">{d}</span>
              <span>{hs.length ? hs.map((h) => `${minutesToHHMM(h.opensAt)}–${minutesToHHMM(h.closesAt)}`).join(', ') : 'Fechado'}</span>
            </li>
          );
        })}
      </ul>
    </details>
  );
}

function Unavailable({ r, message }: { r: TenantRestaurant; message?: string }) {
  return (
    <main className="grid min-h-dvh place-items-center px-4 text-center">
      <div className="max-w-sm">
        <RestaurantAvatar r={r} className="mx-auto size-16" />
        <h1 className="mt-4 font-display text-2xl font-semibold">{r.name}</h1>
        <p className="mt-3 inline-flex items-center gap-1.5 text-ink-3"><Clock className="size-4" /> {message ?? 'Pedidos online indisponíveis no momento. Chame um atendente.'}</p>
        <p className="mt-6"><Link to={staffPath('/login')} className="text-sm text-ink-3 underline">Área do Funcionário</Link></p>
      </div>
    </main>
  );
}
